import { Grid, Paper, Tab, Tabs } from '@mui/material';

import { TFunction } from 'i18next';
import { useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';

import LeihBar from 'assets/leihBar_Logo.svg';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import { CategoriesList } from '../Categories/CategoriesList';
import { LocationsList } from '../Locations/LocationsList';
import { OrderOverview } from '../Orders';
import { ProductList } from '../Products/ProductList';
import { isLeihBarAdmin, isLeihBarLender } from '../helper';

export enum TabPanels {
  PRODUCTS = 'products',
  CATEGORIES = 'categories',
  LOCATIONS = 'locations',
  ORDERS = 'orders'
}

interface TabConfig {
  [key: string]: React.ReactNode;
}

const tabConfigs: { [key: string]: TabConfig } = {
  admin: {
    [TabPanels.PRODUCTS]: <ProductList />,
    [TabPanels.CATEGORIES]: <CategoriesList />,
    [TabPanels.LOCATIONS]: <LocationsList />,
    [TabPanels.ORDERS]: <OrderOverview />
  },
  lender: {
    [TabPanels.PRODUCTS]: <ProductList />,
    [TabPanels.ORDERS]: <OrderOverview />
  }
};

const renderTabs = (
  tabConfig: TabConfig,
  value: string,
  setValue: (value: string) => void,
  t: TFunction
) => {
  return (
    <Tabs value={value} onChange={(_, newValue) => setValue(newValue)} indicatorColor="primary">
      {Object.keys(tabConfig).map(tab => (
        <Tab
          value={tab}
          key={tab}
          label={t(translations.pages.leihbar.tabs[tab.toLowerCase()]())}
        />
      ))}
    </Tabs>
  );
};

export const Overview = () => {
  const { t } = useTranslation();
  const { permissions } = useAuthContext();
  const isAdmin = isLeihBarAdmin(permissions);
  const isLender = isLeihBarLender(permissions);
  usePageHeading({ heading: t(translations.pages.leihbar.title()), headingLogo: LeihBar });

  const [tab, setTab] = useQueryState('tab', { defaultValue: TabPanels.PRODUCTS, shallow: false });

  const renderTabContent = () => {
    if (permissions.length === 0) return null;
    if (isAdmin) {
      return tabConfigs.admin[tab] || null;
    } else if (isLender) {
      return tabConfigs.lender[tab] || null;
    } else {
      return tabConfigs.lender[TabPanels.PRODUCTS];
    }
  };

  const renderTabsComponent = () => {
    if (isAdmin) {
      return renderTabs(tabConfigs.admin, tab, setTab, t);
    } else if (isLender) {
      return renderTabs(tabConfigs.lender, tab, setTab, t);
    } else {
      return null;
    }
  };

  return (
    <Grid container>
      <Paper style={{ minHeight: '45em', paddingBottom: 0, width: '100%' }}>
        {renderTabsComponent()}
        {renderTabContent()}
      </Paper>
    </Grid>
  );
};
